import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import {
  WxrlRuleDetail,
  WxrlCondition,
  WxrlAction,
  WxrlRuleTrigger,
  WrxlRulesService,
  WxrlOperator,
} from '../../services/wxrl-rules.service';
import {
  formatTriggerElementoUiLabel,
  spFormatActionHeaderDetail,
  spFormatActionStepDetail,
  spFormatConditionDetail,
  spFormatTriggerDetail,
} from '../../utils/wxrl-treetable-sp-format';
import { normalizeSnFlag, normalizeVisibleFlag } from '../../utils/wxrl-sn-flag';
import { isDirectCondition, WXRL_DIRECT_CONDITION } from '../../utils/wxrl-direct';
import { TreeNode, ConfirmationService } from 'primeng/api';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'afoone-wxrl-schema',
  templateUrl: './wxrl-schema.component.html',
  styleUrls: ['./wxrl-schema.component.css'],
  providers: [ConfirmationService],
})
export class WxrlSchemaComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public rule: WxrlRuleDetail = null;
  @Input() public conditionsFormArray: FormArray;
  @Input() public actionsFormArray: FormArray;
  @Input() public triggersFormArray: FormArray;
  @Input() public canEdit: boolean = false;

  // Getter para determinar si realmente se puede editar
  get effectiveCanEdit(): boolean {
    // En modo nuevo, siempre permitir edición para poder añadir condiciones
    return this.canEdit;
  }
  @Input() public ruleForm: FormGroup;
  @Input() public labNr?: number;
  @Input() public labType?: string;
  @Input() public isNewRule: boolean = false;

  /** Padre muestra confirmación y navega al catálogo si el usuario abandona el primer acceso (cabecera auto-abierta). */
  @Output() newRuleFirstAccessAbandon = new EventEmitter<void>();

  /** Lab resuelto para bindings hijos; sin laboratorio ficticio. */
  get effectiveLabNrForChildren(): number | undefined {
    return this.resolveEffectiveLabNr();
  }

  /** Entero de laboratorio > 0 desde input o regla cargada. */
  private resolveEffectiveLabNr(): number | undefined {
    const raw = this.isNewRule
      ? this.labNr ?? this.rule?.labNr ?? this.rule?.actionHeader?.labNr
      : this.rule?.labNr ?? this.rule?.actionHeader?.labNr ?? this.labNr;
    if (raw == null || (typeof raw === 'string' && String(raw).trim() === '')) {
      return undefined;
    }
    const n = typeof raw === 'string' && String(raw).trim() !== '' ? Number(String(raw).trim()) : Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
      return undefined;
    }
    return n;
  }

  public treeNodes: TreeNode[] = [];

  // Drawers
  public conditionDrawerVisible: boolean = false;
  public actionDrawerVisible: boolean = false;
  public triggerDrawerVisible: boolean = false;
  public headerDrawerVisible: boolean = false;
  public formulaDrawerVisible: boolean = false;

  // Formularios
  public conditionForm: FormGroup;
  public actionForm: FormGroup;
  public triggerForm: FormGroup;
  public headerForm: FormGroup;

  // Estados de edición
  public editingConditionIndex: number = -1;
  public editingActionIndex: number = -1;
  public editingTriggerIndex: number = -1;
  public isEditModeCondition: boolean = false;
  public isEditModeAction: boolean = false;
  public isEditModeTrigger: boolean = false;

  // Datos que se están editando en los drawers
  public editingCondition: WxrlCondition | null = null;
  public editingAction: WxrlAction | null = null;
  public editingTrigger: WxrlRuleTrigger | null = null;
  public editingHeaderData: any = null;

  // Propiedad para initialCondRule (para pasar al drawer cuando se crea una nueva condición)
  public initialCondRule: string | null = null;

  // Opciones de dropdown - se inicializan en ngOnInit
  public operatorOptions: any[] = [];
  public booleanOptions: any[] = [];
  public actionStates: any[] = [];
  public actionTypes: any[] = [];
  public subjectTypes: any[] = [];
  public eventTypes: any[] = [];
  public ruleStates: any[] = [];

  // Opciones dinámicas para formulario de condición
  public actuaSobreOptions: any[] = [];
  public dynamicOperatorOptions: WxrlOperator[] = [];
  public elementoOptions: any[] = [];
  public perfilOptions: any[] = [];
  public entryValueOptions: any[] = [];
  public aislamientoOptions: any[] = [
    { label: 'A', value: 'A' },
    { label: 'B', value: 'B' },
  ];
  public tipoResOptions: any[] = [];
  public ruleActionOptions: { label: string; value: number }[] = [];
  public selectedOperatorInfo: string = '';

  // Control de visibilidad de campos dinámicos
  public enableV1: boolean = false;
  public enableV1Elemento: boolean = false;
  public multiSelectV1: boolean = false;
  public enableV2: boolean = false;
  public enableV2Dropdown: boolean = false;
  public multiSelectV2: boolean = false;

  // Datos del operador seleccionado para queries dinámicas
  private currentOperatorData: any = null;
  private isLoadingOperatorOptions: boolean = false;

  /** LabelAction por ActionKrz (catálogo laboratorio) para detalle tipo SP en pasos. */
  private actionLabelByKrz: Map<string, string> = new Map();

  private lastActionCatalogLab: number | null = null;

  /** Reconstruir TreeTable cuando el FormArray cambia sin cambiar la referencia del @Input (#705). */
  private conditionsTreeSyncSub?: Subscription;

  /**
   * Evita abrir varias veces el drawer de cabecera al crear una regla nueva.
   * La primera vez que el árbol está listo (`buildTree`) se fuerza la apertura (primer acceso).
   */
  private newRuleFirstAccessDone = false;

  /**
   * True solo entre la apertura automática inicial del drawer de cabecera y el primer Guardar de cabecera.
   * En ese tramo, Cancel en el drawer pide confirmar abandono total y volver al catálogo.
   * Propiedad explícita (no getter) para evitar flickers de CD con el diálogo de confirmación (#698).
   */
  headerDrawerFirstAccessUx = false;

  constructor(
    private _fb: FormBuilder,
    private _confirmationService: ConfirmationService,
    private _cdr: ChangeDetectorRef,
    private _translate: TranslateService,
    private _wxrlService: WrxlRulesService
  ) {
    this.initializeForms();
  }

  private initializeForms(): void {
    // Formulario de condición
    this.conditionForm = this._fb.group({
      condRule: ['', Validators.required],
      posC: [0], // Se calcula automáticamente, no es visible para el usuario
      kommentar: ['', [Validators.maxLength(255)]],
      condId: ['', Validators.required],
      saveValue: [''],
      heredar: [false],
      grupNr: [''],
      rOperator: ['', Validators.required],
      labelOperator: [''],
      entryValue: [''],
      negCond: ['N'],
      exitCond: ['N'],
      visible: ['S'],
      isolate: [''],
      tipoRes: [''],
    });

    // Las suscripciones a valueChanges ahora se manejan en el componente del drawer

    // Formulario de acción
    this.actionForm = this._fb.group({
      ruleState: ['', Validators.required],
      posA: [0, Validators.required],
      ruleActionDesc: ['', Validators.required],
      actionKrz: ['', Validators.required],
      methNr: [''],
      grupNr: [''],
      textNr: [''],
      actionValue: [''],
      additional1: [''],
      additional2: [''],
      kommentar: [''],
    });

    // Formulario de disparador
    this.triggerForm = this._fb.group({
      subjectType: ['', Validators.required],
      subjectID: [null, Validators.required],
      subjectName: ['', Validators.required],
      methNrFilter: [''],
      alarmFilter: [''],
      posR: [0, Validators.required],
      ruleState: ['', Validators.required],
      triggEvent: ['', Validators.required],
      auftrNrTest: [''],
      debug: [0],
      kommentar: [''],
    });

    // Formulario de cabecera
    this.headerForm = this._fb.group({
      ruleNr: [{ value: 0, disabled: true }],
      ruleName: ['', Validators.required],
      ruleKrz: ['', [Validators.required, Validators.maxLength(10)]],
      kommentar: [''],
      condition: [{ value: '', disabled: true }],
      negRule: [false],
      ruleActionNr: [null, this.numericValidator],
    });
  }

  async ngOnInit(): Promise<void> {
    this.initializeDropdownOptions();
    await this.loadActuaSobreOptions();
    await this.loadActionStates();
    await this.loadActionCatalog();
    await this.loadRuleActionOptions();
    this.bindConditionsFormArrayForTree();
    this.scheduleBuildTree();
  }

  ngOnDestroy(): void {
    this.conditionsTreeSyncSub?.unsubscribe();
  }

  private bindConditionsFormArrayForTree(): void {
    this.conditionsTreeSyncSub?.unsubscribe();
    this.conditionsTreeSyncSub = undefined;
    if (!this.conditionsFormArray) {
      return;
    }
    this.conditionsTreeSyncSub = this.conditionsFormArray.valueChanges.subscribe(
      () => {
        this.scheduleBuildTree();
      }
    );
  }

  private initializeDropdownOptions(): void {
    this.operatorOptions = [
      {
        label: this._translate.instant('WXRL.SCHEMA.OPERATORS.IGUAL'),
        value: '=',
      },
      {
        label: this._translate.instant('WXRL.SCHEMA.OPERATORS.DISTINTO'),
        value: '!=',
      },
      {
        label: this._translate.instant('WXRL.SCHEMA.OPERATORS.MAYOR'),
        value: '>',
      },
      {
        label: this._translate.instant('WXRL.SCHEMA.OPERATORS.MAYOR_IGUAL'),
        value: '>=',
      },
      {
        label: this._translate.instant('WXRL.SCHEMA.OPERATORS.MENOR'),
        value: '<',
      },
      {
        label: this._translate.instant('WXRL.SCHEMA.OPERATORS.MENOR_IGUAL'),
        value: '<=',
      },
    ];

    this.booleanOptions = [
      { label: this._translate.instant('WXRL.SCHEMA.BOOLEAN.SI'), value: 'S' },
      { label: this._translate.instant('WXRL.SCHEMA.BOOLEAN.NO'), value: 'N' },
    ];

    // Cargar estados de acción desde backend
    this.loadActionStates();

    this.subjectTypes = [
      {
        label: this._translate.instant('WXRL.SCHEMA.SUBJECT_TYPES.TIPO_1'),
        value: 'TYPE1',
      },
      {
        label: this._translate.instant('WXRL.SCHEMA.SUBJECT_TYPES.TIPO_2'),
        value: 'TYPE2',
      },
    ];

    this.eventTypes = [
      {
        label: this._translate.instant('WXRL.SCHEMA.EVENT_TYPES.EVENTO_1'),
        value: 'EVENT1',
      },
      {
        label: this._translate.instant('WXRL.SCHEMA.EVENT_TYPES.EVENTO_2'),
        value: 'EVENT2',
      },
    ];

    this.ruleStates = [
      {
        label: this._translate.instant('WXRL.SCHEMA.STATES.ACTIVO'),
        value: 'ACTIVE',
      },
      {
        label: this._translate.instant('WXRL.SCHEMA.STATES.INACTIVO'),
        value: 'INACTIVE',
      },
    ];
  }

  // Cargar opciones de "Actúa Sobre" desde RULESCATALOGCONDITION
  async loadActuaSobreOptions(): Promise<void> {
    try {
      const result = await this._wxrlService.getCatalogConditions();
      this.actuaSobreOptions = result.map((item) => ({
        label: item.label,
        value: item.condId,
      }));
    } catch (error) {
      console.error('Error loading Actúa Sobre options:', error);
      this.actuaSobreOptions = [];
    }
  }

  // Cargar estados de acción desde backend (solo DB, sin fallback traducido)
  async loadActionStates(): Promise<void> {
    try {
      const result = await this._wxrlService.getActionStates();
      this.actionStates = result.map((state) => ({
        label: state.schluessel,
        value: state.schl,
      }));
    } catch (error) {
      console.error('Error loading action states:', error);
      this.actionStates = [];
    }
  }

  /**
   * Fila de paso de acción en TreeTable con estado distinto de RXAC: estilo “no activo”, sin bloquear edición (#699).
   */
  isActionTreeRowNonActive(rowData: { type?: string; action?: WxrlAction }): boolean {
    if (rowData?.type !== 'action' || !rowData.action) {
      return false;
    }
    return this.resolveActionStateToCode(rowData.action.ruleState) !== 'RXAC';
  }

  /** Disparador con RuleState distinto de RXAC: misma lógica visual que pasos de acción (#700). */
  isTriggerTreeRowNonActive(rowData: {
    type?: string;
    trigger?: WxrlRuleTrigger;
  }): boolean {
    if (rowData?.type !== 'trigger' || !rowData.trigger) {
      return false;
    }
    return this.resolveActionStateToCode(rowData.trigger.ruleState) !== 'RXAC';
  }

  /** Fila atenuada en TreeTable: paso o disparador no RXAC (#699 / #700). */
  isTreeTableStateRowMuted(rowData: {
    type?: string;
    action?: WxrlAction;
    trigger?: WxrlRuleTrigger;
  }): boolean {
    return (
      this.isActionTreeRowNonActive(rowData) ||
      this.isTriggerTreeRowNonActive(rowData)
    );
  }

  /** Resuelve etiqueta (Schluessel) a código (Schl) para persistir en backend. */
  private resolveActionStateToCode(ruleState: string | undefined): string {
    if (ruleState == null || ruleState === '') return ruleState ?? '';
    if (!this.actionStates?.length) return ruleState;
    const match = this.actionStates.find(
      (s: any) =>
        s.value === ruleState ||
        (s.label && String(s.label).toLowerCase() === String(ruleState).toLowerCase())
    );
    return match?.value ?? ruleState;
  }

  /**
   * Etiqueta de estado (Typ 126) para badge TreeTable: descripción API si existe, si no catálogo (#700).
   */
  formatTreeTableRuleStateLabel(
    ruleState: string | undefined,
    ruleStateDescription?: string | null
  ): string {
    const desc =
      ruleStateDescription != null &&
      String(ruleStateDescription).trim() !== ''
        ? String(ruleStateDescription).trim()
        : '';
    if (desc) {
      return desc;
    }
    if (ruleState == null || ruleState === '') {
      return '';
    }
    if (!this.actionStates?.length) {
      return String(ruleState);
    }
    const rs = String(ruleState);
    const match = this.actionStates.find(
      (s: any) =>
        s.value === rs ||
        (s.label && String(s.label).toLowerCase() === rs.toLowerCase())
    );
    return match?.label ?? rs;
  }

  treeTableRuleStateBadgeVisible(rowData: {
    type?: string;
    action?: WxrlAction;
    trigger?: WxrlRuleTrigger;
  }): boolean {
    return this.treeTableRuleStateLabelForRow(rowData) !== '';
  }

  treeTableRuleStateLabelForRow(rowData: {
    type?: string;
    action?: WxrlAction;
    trigger?: WxrlRuleTrigger;
  }): string {
    if (rowData?.type === 'action' && rowData.action) {
      return this.formatTreeTableRuleStateLabel(rowData.action.ruleState);
    }
    if (rowData?.type === 'trigger' && rowData.trigger) {
      return this.formatTreeTableRuleStateLabel(
        rowData.trigger.ruleState,
        rowData.trigger.ruleStateDescription
      );
    }
    return '';
  }

  treeTableRuleStateTooltip(rowData: {
    type?: string;
    action?: WxrlAction;
    trigger?: WxrlRuleTrigger;
  }): string {
    const state = this.treeTableRuleStateLabelForRow(rowData);
    return this._translate.instant('WXRL.SCHEMA.RULE_STATE_BADGE_TOOLTIP', {
      state,
    });
  }

  // Cargar catálogo de acciones desde backend
  async loadActionCatalog(): Promise<void> {
    const labNr = this.resolveEffectiveLabNr();
    if (labNr == null) {
      this._wxrlService.handleError(new Error('WXRL.ERRORS.LAB_NR_REQUIRED'));
      this.lastActionCatalogLab = null;
      this.actionLabelByKrz = new Map();
      this.actionTypes = [];
      return;
    }
    if (
      this.lastActionCatalogLab === labNr &&
      this.actionLabelByKrz.size > 0
    ) {
      return;
    }
    try {
      const result = await this._wxrlService.getActionCatalog(labNr);
      this.lastActionCatalogLab = labNr;
      this.actionLabelByKrz = new Map(
        result.map((a) => [a.actionKrz, a.labelAction])
      );
      this.actionTypes = result.map((action) => ({
        label: action.labelAction,
        value: action.actionKrz,
      }));
    } catch (error) {
      console.error('Error loading action catalog:', error);
      this.lastActionCatalogLab = null;
      this.actionLabelByKrz = new Map();
      this.actionTypes = [];
      this._wxrlService.handleError(error);
    }
  }

  // Cargar operadores dinámicos según "Actúa Sobre"
  async loadDynamicOperators(condId: string): Promise<void> {
    console.log('loadDynamicOperators', condId);
    if (!condId) {
      this.dynamicOperatorOptions = [];
      this.resetDynamicFields();
      return;
    }

    try {
      const result = await this._wxrlService.getOperatorsByCondId(condId);
      console.log('retrieved dynamic operators:', result);
      this.dynamicOperatorOptions = result;
    } catch (error) {
      console.error('Error loading dynamic operators:', error);
      this.dynamicOperatorOptions = [];
      this.resetDynamicFields();
    }
  }

  // Manejar cambio en "Actúa Sobre"
  async onActuaSobreChange(): Promise<void> {
    const condId = this.conditionForm.get('condId')?.value;
    if (condId) {
      await this.loadDynamicOperators(condId);
      // Resetear campos dependientes
      this.conditionForm.patchValue({
        rOperator: '',
        labelOperator: '',
        saveValue: '',
        entryValue: '',
        heredar: false,
      });
      // Preservar las opciones de operadores que acabamos de cargar
      this.resetDynamicFields(true);
    } else {
      this.resetDynamicFields();
    }
  }

  // Manejar cambio en "Operador"
  async onOperatorChange(): Promise<void> {
    // Prevenir llamadas múltiples simultáneas
    if (this.isLoadingOperatorOptions) {
      return;
    }

    const posR = this.conditionForm.get('rOperator')?.value;
    if (posR && this.dynamicOperatorOptions.length > 0) {
      this.currentOperatorData = this.dynamicOperatorOptions.find(
        (op) => op.posR === posR
      );
      if (this.currentOperatorData) {
        this.enableV1 = this.currentOperatorData.enableV1 === 1;
        this.multiSelectV1 = this.currentOperatorData.multiSelectV1 === 1;
        this.enableV2 = this.currentOperatorData.enableV2 === 1;
        this.multiSelectV2 = this.currentOperatorData.multiSelectV2 === 1;
        this.selectedOperatorInfo = this.currentOperatorData.info || '';

        // Cargar datos dinámicos usando idOperator para determinar qué controles mostrar
        this.isLoadingOperatorOptions = true;
        try {
          // Si EnableV1=1, intentar cargar opciones para "ID Elemento"
          // Si hay opciones disponibles, mostrar dropdown; si no, el campo no se mostrará
          if (this.enableV1 && this.currentOperatorData.idOperator) {
            await this.loadElementoOptions(this.currentOperatorData.idOperator);
            // enableV1Elemento se establece en loadElementoOptions según si hay opciones
          } else {
            setTimeout(() => {
              this.enableV1Elemento = false;
              this.elementoOptions = [];
              this._cdr.markForCheck();
            }, 0);
          }

          // Si EnableV2=1, intentar cargar opciones para "Valor de Entrada"
          // Si hay opciones, mostrar dropdown; si no, mostrar textbox
          if (this.enableV2 && this.currentOperatorData.idOperator) {
            await this.loadEntryValueOptions(this.currentOperatorData.idOperator);
            // enableV2Dropdown se establece en loadEntryValueOptions según si hay opciones
          } else {
            setTimeout(() => {
              this.enableV2Dropdown = false;
              this.entryValueOptions = [];
              this._cdr.markForCheck();
            }, 0);
          }
        } finally {
          this.isLoadingOperatorOptions = false;
        }

        // Actualizar validación
        setTimeout(() => {
          this.updateDynamicValidation();
          this._cdr.markForCheck();
        }, 0);
      }
    } else {
      this.resetDynamicFields(true);
    }
  }

  // Parsear saveValue al cargar para edición
  // Si es multiselect y viene como string separado por comas, convertirlo a array
  private parseSaveValueForEdit(saveValue: any): any {
    if (saveValue === '-1' || !saveValue) {
      return '';
    }
    if (this.multiSelectV1 && typeof saveValue === 'string' && saveValue.includes(',')) {
      return saveValue.split(',').filter(v => v.trim() !== '');
    }
    return saveValue;
  }

  // Cargar opciones de "ID Elemento" según idOperator
  // Si hay opciones disponibles, se muestra como dropdown (enableV1Elemento = true)
  // Si no hay opciones, el campo no se muestra (enableV1Elemento = false)
  async loadElementoOptions(idOperator: string): Promise<void> {
    try {
      const condId = this.conditionForm.get('condId')?.value;
      const result = await this._wxrlService.getElementoOptions(
        idOperator,
        condId,
        this.effectiveLabNrForChildren
      );
      if (result && result.length > 0) {
        this.elementoOptions = result.map(item => ({
          label: item.label,
          value: item.value
        }));
        // Si hay opciones, mostrar el dropdown
        // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.enableV1Elemento = true;
          this._cdr.markForCheck();
        }, 0);
      } else {
        // Si no hay opciones (QRY1 vacío o sin resultados), no mostrar el campo
        this.elementoOptions = [];
        setTimeout(() => {
          this.enableV1Elemento = false;
          this._cdr.markForCheck();
        }, 0);
      }
    } catch (error) {
      console.error('Error loading elemento options:', error);
      this.elementoOptions = [];
      setTimeout(() => {
        this.enableV1Elemento = false;
        this._cdr.markForCheck();
      }, 0);
    }
  }

  // Cargar opciones de "Valor de Entrada" según idOperator
  // Si hay opciones disponibles, se muestra como dropdown (enableV2Dropdown = true)
  // Si no hay opciones (QRY2 vacío), se muestra como textbox (enableV2Dropdown = false)
  async loadEntryValueOptions(idOperator: string): Promise<void> {
    try {
      const condId = this.conditionForm.get('condId')?.value;
      const result = await this._wxrlService.getEntryValueOptions(
        idOperator,
        condId,
        this.effectiveLabNrForChildren
      );
      if (result && result.length > 0) {
        this.entryValueOptions = result.map(item => ({
          label: item.label,
          value: item.value
        }));
        // Si hay opciones, mostrar el dropdown
        // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.enableV2Dropdown = true;
          this._cdr.markForCheck();
        }, 0);
      } else {
        // Si no hay opciones (QRY2 vacío), mostrar textbox
        this.entryValueOptions = [];
        setTimeout(() => {
          this.enableV2Dropdown = false;
          this._cdr.markForCheck();
        }, 0);
      }
    } catch (error) {
      console.error('Error loading entry value options:', error);
      this.entryValueOptions = [];
      // En caso de error, mostrar textbox
      setTimeout(() => {
        this.enableV2Dropdown = false;
        this._cdr.markForCheck();
      }, 0);
    }
  }

  // Cargar opciones de "Perfil"
  async loadPerfilOptions(): Promise<void> {
    try {
      const labNr = this.resolveEffectiveLabNr();
      if (labNr == null) {
        this._wxrlService.handleError(new Error('WXRL.ERRORS.LAB_NR_REQUIRED'));
        this.perfilOptions = [];
        return;
      }
      const result = await this._wxrlService.getPerfiles(labNr);
      this.perfilOptions = result.map((item) => ({
        label: item.grupName,
        value: item.grupNr,
      }));
    } catch (error) {
      console.error('Error loading perfil options:', error);
      this.perfilOptions = [];
    }
  }

  // Cargar opciones de "Tipo de Resultado"
  async loadTipoResOptions(): Promise<void> {
    try {
      const result = await this._wxrlService.getTipoRes();
      this.tipoResOptions = result.map((item) => ({
        label: item.schluessel,
        value: item.schl,
      }));
    } catch (error) {
      console.error('Error loading tipo res options:', error);
      this.tipoResOptions = [];
    }
  }

  // Validador personalizado para ruleActionNr: si tiene valor, debe ser numérico
  private numericValidator(control: any): { [key: string]: any } | null {
    if (
      control.value === null ||
      control.value === undefined ||
      control.value === ''
    ) {
      return null; // Campo vacío es válido (no requerido)
    }
    const value =
      typeof control.value === 'number' ? control.value : Number(control.value);
    if (Number.isNaN(value) || !Number.isInteger(value)) {
      return { numeric: true }; // Error si no es numérico
    }
    return null; // Válido
  }

  // Cargar opciones de "ID Acción"
  async loadRuleActionOptions(): Promise<void> {
    try {
      const labNr = this.resolveEffectiveLabNr();
      if (labNr == null) {
        this._wxrlService.handleError(new Error('WXRL.ERRORS.LAB_NR_REQUIRED'));
        this.ruleActionOptions = [];
        return;
      }
      const result = await this._wxrlService.getRuleActions(labNr);
      this.ruleActionOptions = result.map((item) => ({
        label: item.descripcion,
        value: item.ruleActionNr,
      }));
    } catch (error) {
      console.error('Error loading rule action options:', error);
      this.ruleActionOptions = [];
    }
  }

  // Resetear campos dinámicos
  private resetDynamicFields(preserveOperatorOptions: boolean = false): void {
    console.log('resetDynamicFields');
    this.enableV1 = false;
    this.enableV1Elemento = false;
    this.multiSelectV1 = false;
    this.enableV2 = false;
    this.enableV2Dropdown = false;
    this.multiSelectV2 = false;
    this.selectedOperatorInfo = '';
    this.currentOperatorData = null;
    this.elementoOptions = [];
    this.entryValueOptions = [];
    // Solo borrar las opciones de operadores si no se deben preservar
    if (!preserveOperatorOptions) {
      this.dynamicOperatorOptions = [];
    }
  }

  // Actualizar validación dinámica
  private updateDynamicValidation(): void {
    const saveValueControl = this.conditionForm.get('saveValue');
    const entryValueControl = this.conditionForm.get('entryValue');

    // Validar saveValue si enableV1Elemento es true y heredar es false
    if (this.enableV1Elemento) {
      const heredar = this.conditionForm.get('heredar')?.value;
      if (!heredar) {
        saveValueControl?.setValidators([Validators.required]);
      } else {
        saveValueControl?.clearValidators();
      }
      saveValueControl?.updateValueAndValidity();
    } else {
      saveValueControl?.clearValidators();
      saveValueControl?.updateValueAndValidity();
    }

    // Validar entryValue si enableV2Dropdown es true
    if (this.enableV2Dropdown) {
      entryValueControl?.setValidators([Validators.required]);
    } else {
      entryValueControl?.clearValidators();
    }
    entryValueControl?.updateValueAndValidity();
  }

  // Actualizar validación de saveValue según checkbox Heredar
  private updateSaveValueValidation(): void {
    this.updateDynamicValidation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conditionsFormArray']) {
      this.bindConditionsFormArrayForTree();
    }
    if (
      changes['labNr'] &&
      !changes['labNr'].firstChange &&
      changes['labNr'].previousValue !== changes['labNr'].currentValue
    ) {
      this.lastActionCatalogLab = null;
      void this.loadActionCatalog()
        .then(() => this.loadRuleActionOptions())
        .then(() => this.scheduleBuildTree());
      return;
    }
    if (
      changes['rule'] &&
      !changes['rule'].firstChange &&
      changes['rule'].previousValue !== changes['rule'].currentValue
    ) {
      this.lastActionCatalogLab = null;
      void this.loadActionCatalog()
        .then(() => this.loadRuleActionOptions())
        .then(() => this.scheduleBuildTree());
      return;
    }
    this.scheduleBuildTree();
  }

  private scheduleBuildTree(): void {
    if (this.rule) {
      setTimeout(() => {
        this.buildTree();
        this._cdr.markForCheck();
      }, 0);
    }
  }

  /**
   * Condiciones mostradas en el esquema: si hay FormArray de edición, es la fuente de verdad
   * (evita filas obsoletas tras clear desde cabecera o DIRECT) (#705).
   */
  private getConditionsForTree(): WxrlCondition[] {
    if (!this.conditionsFormArray) {
      return this.rule?.conditions ?? [];
    }
    return this.conditionsFormArray.controls.map((ctrl, index) => {
      const v = ctrl.value as Record<string, unknown>;
      const rawIdOperator = v['idOperator'];
      const idOperator =
        typeof rawIdOperator === 'string' || typeof rawIdOperator === 'number'
          ? String(rawIdOperator).trim()
          : '';
      return {
        condRule: String(v['condRule'] ?? ''),
        posC: Number(v['posC'] ?? index + 1),
        kommentar: String(v['kommentar'] ?? ''),
        condId: String(v['condId'] ?? ''),
        saveValue: String(v['saveValue'] ?? ''),
        methName: String(v['methName'] ?? ''),
        grupNr: Number(v['grupNr'] ?? 0),
        rOperator: String(v['rOperator'] ?? ''),
        idOperator: idOperator === '' ? undefined : idOperator,
        catalogROperator: String(v['catalogROperator'] ?? ''),
        labelOperator: String(v['labelOperator'] ?? ''),
        entryValue: String(v['entryValue'] ?? ''),
        negCond: normalizeSnFlag(v['negCond'], 'N'),
        exitCond: normalizeSnFlag(v['exitCond'], 'N'),
        visible: normalizeVisibleFlag(v['visible'], 'S'),
        isolate: String(v['isolate'] ?? ''),
        tipoRes: String(v['tipoRes'] ?? ''),
        debugCond: (v['debugCond'] as number | null | undefined) ?? null,
        relation: (v['relation'] as string | null | undefined) ?? null,
      };
    });
  }

  private buildTree(): void {
    if (!this.rule) {
      this.treeNodes = [];
      return;
    }

    const rootNodes: TreeNode[] = [];

    // Nodo Regla solo con Fórmula (mostrar cuando hay rule, aunque fórmula vacía)
    const hasCondition = this.rule?.condition;
    if (hasCondition || !!this.rule) {
      rootNodes.push({
        data: {
          elemento: this._translate.instant('WXRL.SCHEMA.REGLA'),
          detalle: '',
          type: 'rule',
        },
        expanded: true,
        children: [
          {
            data: {
              elemento: this._translate.instant('WXRL.SCHEMA.FORMULA'),
              detalle: isDirectCondition(this.rule?.condition)
                ? this._translate.instant('WXRL.SCHEMA.FORMULA_DIRECT_SHORT')
                : this.rule?.condition || '',
              type: 'formula',
            },
            leaf: true,
          },
        ],
      });
    }

    // Nodo de Condiciones
    const conditionsForTree = this.getConditionsForTree();
    const hasConditions = conditionsForTree.length > 0;
    if (hasConditions || !!this.rule) {
      const conditionNodes: TreeNode[] = hasConditions
        ? conditionsForTree.map((condition, index) => ({
            data: {
              elemento: condition.condRule || `${this._translate.instant('WXRL.SCHEMA.NEW_CONDITION')} ${index + 1}`,
              detalle: this.formatConditionDetail(condition),
              type: 'condition',
              condition: condition,
              index: index,
            },
            leaf: true,
          }))
        : [];

      rootNodes.push({
        data: {
          elemento: this._translate.instant('WXRL.SCHEMA.CONDICIONES'),
          detalle: '',
          type: 'section',
        },
        expanded: true,
        children: conditionNodes,
      });
    }

    // Nodo de Acciones: todos los pasos (RXAC, RXIN, etc.). El SP wspi_XRules_TreeTable sigue filtrando solo RXAC (#699).
    if (this.rule) {
      const actionNodes: TreeNode[] = [];
      const hdr = this.rule.actionHeader;
      if (
        hdr &&
        (hdr.ruleActionNr != null ||
          (hdr.ruleActionDesc && String(hdr.ruleActionDesc).trim() !== ''))
      ) {
        actionNodes.push({
          data: {
            elemento: this._translate.instant('WXRL.SCHEMA.RESUMEN'),
            detalle: spFormatActionHeaderDetail(
              hdr.ruleActionNr,
              hdr.ruleActionDesc
            ),
            type: 'action-summary',
          },
          leaf: true,
        });
      }

      const actions = this.rule.actions || [];
      for (let formArrayIndex = 0; formArrayIndex < actions.length; formArrayIndex++) {
        const action = actions[formArrayIndex];
        const labelFull = this.resolveActionStepLabel(action);
        actionNodes.push({
          data: {
            elemento: String(action.posA),
            detalle: this.formatActionDetail(action),
            type: 'action',
            action,
            index: formArrayIndex,
            /** Texto íntegro de LabelAction/código; tooltip si >20 chars (ver formatActionLabelColumn). */
            actionLabelTooltip:
              labelFull.length > 20 ? labelFull : undefined,
          },
          leaf: true,
        });
      }

      rootNodes.push({
        data: {
          elemento: this._translate.instant('WXRL.SCHEMA.ACCIONES'),
          detalle: '',
          type: 'section',
        },
        expanded: true,
        children: actionNodes,
      });
    }

    // Nodo de Disparadores
    const hasTriggers = this.rule.triggers && this.rule.triggers.length > 0;
    if (hasTriggers || !!this.rule) {
      const triggerNodes: TreeNode[] = hasTriggers
        ? this.rule.triggers.map((trigger, index) => ({
            data: {
              elemento: formatTriggerElementoUiLabel(trigger),
              detalle: this.formatTriggerDetail(trigger),
              type: 'trigger',
              trigger,
              index,
            },
            leaf: true,
          }))
        : [];

      rootNodes.push({
        data: {
          elemento: this._translate.instant('WXRL.SCHEMA.DISPARADORES'),
          detalle: '',
          type: 'section',
        },
        expanded: true,
        children: triggerNodes,
      });
    }

    // Los nodos raíz son Regla (con Fórmula) y las secciones
    this.treeNodes = rootNodes;

    // Regla nueva: primer acceso → abrir panel de cabecera (nº sugerido, descripción, etc.)
    if (
      this.isNewRule &&
      this.rule &&
      this.ruleForm &&
      !this.newRuleFirstAccessDone &&
      !this.headerDrawerVisible
    ) {
      this.newRuleFirstAccessDone = true;
      this.headerDrawerFirstAccessUx = true;
      setTimeout(() => {
        void this.onEditHeader();
      }, 0);
    }
  }

  /** Cancel del drawer de cabecera: solo primer acceso automático dispara abandono en el padre. */
  onHeaderDrawerCancel(): void {
    if (this.headerDrawerFirstAccessUx) {
      this.newRuleFirstAccessAbandon.emit();
      return;
    }
    this.closeHeaderDrawer();
  }

  formatConditionDetail(condition: WxrlCondition): string {
    return spFormatConditionDetail(
      condition,
      this._translate.instant('WXRL.SCHEMA.NEGADO'),
      this._translate.instant('WXRL.SCHEMA.SIN_INFORMACION')
    );
  }

  /** Misma cadena que alimenta el bloque inicial de `spFormatActionStepDetail`. */
  private resolveActionStepLabel(action: WxrlAction): string {
    return (
      (action.labelAction && String(action.labelAction).trim()) ||
      this.actionLabelByKrz.get(action.actionKrz) ||
      this.actionTypes.find((o) => o.value === action.actionKrz)?.label ||
      ''
    );
  }

  formatActionDetail(action: WxrlAction): string {
    return spFormatActionStepDetail(
      action,
      this.resolveActionStepLabel(action),
      this._translate.instant('WXRL.SCHEMA.HEREDADO')
    );
  }

  formatTriggerDetail(trigger: WxrlRuleTrigger): string {
    return spFormatTriggerDetail(
      trigger,
      this._translate.instant('WXRL.SCHEMA.SIN_INFORMACION')
    );
  }

  async onEditCondition(rowData: any): Promise<void> {
    if (!this.conditionsFormArray || !rowData.condition) return;

    // Buscar el índice actual en el FormArray basándonos en los datos de la condición
    const index = this.conditionsFormArray.controls.findIndex(
      (control) =>
        control.value.posC === rowData.condition.posC &&
        control.value.condRule === rowData.condition.condRule
    );

    if (index >= 0 && index < this.conditionsFormArray.length) {
      // Cerrar todos los drawers antes de abrir este
      this.closeAllDrawers();

      const conditionControl = this.conditionsFormArray.at(index);

      // Cargar opciones de perfil y tipo res antes de abrir el drawer
      await this.loadPerfilOptions();
      await this.loadTipoResOptions();

      // Establecer la condición que se está editando
      this.isEditModeCondition = true;
      this.editingConditionIndex = index;
      this.editingCondition = conditionControl.value;

      // Abrir el drawer
      this.conditionDrawerVisible = true;
    }
  }

  onDeleteCondition(rowData: any): void {
    if (!this.conditionsFormArray || !rowData.condition) return;

    // Buscar el índice actual en el FormArray basándonos en los datos de la condición
    const index = this.conditionsFormArray.controls.findIndex(
      (control) =>
        control.value.posC === rowData.condition.posC &&
        control.value.condRule === rowData.condition.condRule &&
        control.value.methName === rowData.condition.methName
    );

    if (index >= 0 && index < this.conditionsFormArray.length) {
      const conditionControl = this.conditionsFormArray.at(index);
      const conditionName =
        conditionControl.value.kommentar ||
        conditionControl.value.methName ||
        `${this._translate.instant('WXRL.SCHEMA.NEW_CONDITION')} ${index + 1}`;

      this._confirmationService.confirm({
        message: this._translate
          .instant('WXRL.SCHEMA.CONFIRM_DELETE_CONDITION')
          .replace('{0}', conditionName),
        header: this._translate.instant('WXRL.SCHEMA.CONFIRM_DELETE_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this._translate.instant('WXRL.SCHEMA.YES_DELETE'),
        rejectLabel: this._translate.instant('WXRL.SCHEMA.CANCEL'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => {
          this.conditionsFormArray.removeAt(index);
          // Reordenar posiciones en el FormArray
          this.conditionsFormArray.controls.forEach((control, idx) => {
            control.patchValue({ posC: idx });
          });
          // Actualizar el rule sincronizando desde el FormArray
          this.syncRuleFromFormArrays();
          // Reconstruir el árbol después de eliminar
          setTimeout(() => {
            this.buildTree();
            this._cdr.markForCheck();
          }, 0);
        },
      });
    }
  }

  /** PosA de API Siemens suele ser 1-based (1,2,3); length del array no sirve como siguiente paso. */
  get nextActionPosA(): number {
    const arr = this.actionsFormArray;
    if (!arr?.length) {
      return this.actionsPosAIsOneBased() ? 1 : 0;
    }
    const max = Math.max(
      ...arr.controls.map((c) => {
        const fg = c as FormGroup;
        const raw = fg.getRawValue ? fg.getRawValue() : fg.value;
        return Number(raw?.posA ?? 0);
      })
    );
    return max + 1;
  }

  private actionsPosAIsOneBased(): boolean {
    if (!this.actionsFormArray?.length) {
      return true;
    }
    const vals = this.actionsFormArray.controls.map((c) => {
      const fg = c as FormGroup;
      const raw = fg.getRawValue ? fg.getRawValue() : fg.value;
      return Number(raw?.posA ?? 0);
    });
    return Math.min(...vals) >= 1;
  }

  onEditAction(rowData: any): void {
    if (!this.actionsFormArray || !rowData.action) return;

    const index = this.actionsFormArray.controls.findIndex(
      (control) =>
        control.value.posA === rowData.action.posA &&
        control.value.ruleActionDesc === rowData.action.ruleActionDesc &&
        control.value.actionKrz === rowData.action.actionKrz
    );

    if (index >= 0 && index < this.actionsFormArray.length) {
      // Cerrar visibilidad de drawers sin perder el contexto de edición que vamos a abrir (#557).
      this.conditionDrawerVisible = false;
      this.triggerDrawerVisible = false;
      this.headerDrawerVisible = false;

      const actionControl = this.actionsFormArray.at(index) as FormGroup;
      this.isEditModeAction = true;
      this.editingActionIndex = index;
      this.editingAction = actionControl.getRawValue
        ? actionControl.getRawValue()
        : actionControl.value;

      this.actionDrawerVisible = true;
    }
  }

  onDeleteAction(rowData: any): void {
    if (!this.actionsFormArray || !rowData.action) return;

    // Buscar el índice actual en el FormArray basándonos en los datos de la acción
    const index = this.actionsFormArray.controls.findIndex(
      (control) =>
        control.value.posA === rowData.action.posA &&
        control.value.ruleActionDesc === rowData.action.ruleActionDesc &&
        control.value.actionKrz === rowData.action.actionKrz
    );

    if (index >= 0 && index < this.actionsFormArray.length) {
      const actionControl = this.actionsFormArray.at(index);
      const actionName =
        actionControl.value.ruleActionDesc ||
        `${this._translate.instant('WXRL.SCHEMA.NEW_ACTION')} ${index + 1}`;

      this._confirmationService.confirm({
        message: this._translate
          .instant('WXRL.SCHEMA.CONFIRM_DELETE_ACTION')
          .replace('{0}', actionName),
        header: this._translate.instant('WXRL.SCHEMA.CONFIRM_DELETE_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this._translate.instant('WXRL.SCHEMA.YES_DELETE'),
        rejectLabel: this._translate.instant('WXRL.SCHEMA.CANCEL'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => {
          this.actionsFormArray.removeAt(index);
          // Reordenar posiciones en el FormArray
          this.actionsFormArray.controls.forEach((control, idx) => {
            control.patchValue({ posA: idx });
          });
          // Actualizar el rule sincronizando desde el FormArray
          this.syncRuleFromFormArrays();
          // Reconstruir el árbol después de eliminar
          setTimeout(() => {
            this.buildTree();
            this._cdr.markForCheck();
          }, 0);
        },
      });
    }
  }

  onEditTrigger(rowData: any): void {
    if (!this.triggersFormArray || !rowData.trigger) return;

    const index = this.triggersFormArray.controls.findIndex(
      (control) =>
        control.value.posR === rowData.trigger.posR &&
        control.value.subjectID === rowData.trigger.subjectID &&
        control.value.subjectType === rowData.trigger.subjectType
    );

    if (index >= 0 && index < this.triggersFormArray.length) {
      // Cerrar todos los drawers antes de abrir este
      this.closeAllDrawers();

      const triggerControl = this.triggersFormArray.at(index);
      this.isEditModeTrigger = true;
      this.editingTriggerIndex = index;
      this.editingTrigger = triggerControl.value;

      this.triggerDrawerVisible = true;
    }
  }

  onDeleteTrigger(rowData: any): void {
    if (!this.triggersFormArray || !rowData.trigger) return;

    // Buscar el índice actual en el FormArray basándonos en los datos del disparador
    const index = this.triggersFormArray.controls.findIndex(
      (control) =>
        control.value.posR === rowData.trigger.posR &&
        control.value.subjectID === rowData.trigger.subjectID &&
        control.value.subjectType === rowData.trigger.subjectType
    );

    if (index >= 0 && index < this.triggersFormArray.length) {
      const triggerControl = this.triggersFormArray.at(index);
      const triggerName =
        triggerControl.value.subjectName ||
        `${this._translate.instant('WXRL.SCHEMA.NEW_TRIGGER')} ${index + 1}`;

      this._confirmationService.confirm({
        message: this._translate
          .instant('WXRL.SCHEMA.CONFIRM_DELETE_TRIGGER')
          .replace('{0}', triggerName),
        header: this._translate.instant('WXRL.SCHEMA.CONFIRM_DELETE_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this._translate.instant('WXRL.SCHEMA.YES_DELETE'),
        rejectLabel: this._translate.instant('WXRL.SCHEMA.CANCEL'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => {
          this.triggersFormArray.removeAt(index);
          // Actualizar el rule sincronizando desde el FormArray
          this.syncRuleFromFormArrays();
          // Reconstruir el árbol después de eliminar
          setTimeout(() => {
            this.buildTree();
            this._cdr.markForCheck();
          }, 0);
        },
      });
    }
  }

  onEdit(rowData: any): void {
    if (rowData.type === 'action-summary') {
      return;
    }
    if (rowData.type === 'condition') {
      this.onEditCondition(rowData);
    } else if (rowData.type === 'action') {
      this.onEditAction(rowData);
    } else if (rowData.type === 'trigger') {
      this.onEditTrigger(rowData);
    } else if (rowData.type === 'formula') {
      this.onEditHeader();
    }
  }

  onDelete(rowData: any): void {
    if (rowData.type === 'action-summary') {
      return;
    }
    if (rowData.type === 'condition') {
      this.onDeleteCondition(rowData);
    } else if (rowData.type === 'action') {
      this.onDeleteAction(rowData);
    } else if (rowData.type === 'trigger') {
      this.onDeleteTrigger(rowData);
    }
  }

  onAdd(rowData: any): void {
    if (rowData.type === 'section') {
      // Determinar qué tipo de elemento agregar según la sección
      if (rowData.elemento === this._translate.instant('WXRL.SCHEMA.CONDICIONES')) {
        this.onNewCondition().catch(error => {
          console.error('Error adding new condition:', error);
          this._wxrlService.handleError(error);
        });
      } else if (rowData.elemento === this._translate.instant('WXRL.SCHEMA.ACCIONES')) {
        this.onNewAction().catch(error => {
          console.error('Error adding new action:', error);
          this._wxrlService.handleError(error);
        });
      } else if (rowData.elemento === this._translate.instant('WXRL.SCHEMA.DISPARADORES')) {
        this.onNewTrigger();
      }
    }
  }

  async onNewCondition(): Promise<void> {
    // Cerrar todos los drawers antes de abrir este
    this.closeAllDrawers();
    this.isEditModeCondition = false;
    this.editingConditionIndex = -1;

    // Cargar opciones de perfil y tipo res antes de abrir el drawer
    await this.loadPerfilOptions();
    await this.loadTipoResOptions();

    // Calcular el siguiente código de condición basado en las condiciones existentes
    let nextConditionCode = 'C001'; // Valor por defecto si no hay condiciones

    if (this.conditionsFormArray && this.conditionsFormArray.length > 0) {
      // Obtener todos los códigos de condición existentes
      const existingCodes = this.conditionsFormArray.controls
        .map(control => control.value?.condRule)
        .filter(code => code && code.match(/^C\d{3}$/))
        .map(code => {
          // Extraer el número del código (ej: "C001" -> 1)
          const numStr = code.substring(1);
          return parseInt(numStr, 10);
        });

      if (existingCodes.length > 0) {
        // Encontrar el máximo número y sumarle 1
        const maxNum = Math.max(...existingCodes);
        const nextNum = maxNum + 1;
        // Formatear como C001, C002, etc.
        nextConditionCode = `C${nextNum.toString().padStart(3, '0')}`;
      }
    }

    // Crear condición inicial con el código generado
    const newCondition: WxrlCondition = {
      condRule: nextConditionCode,
      posC: this.conditionsFormArray?.length || 0,
      kommentar: '',
      condId: '',
      saveValue: '',
      methName: '',
      grupNr: 0,
      rOperator: '',
      labelOperator: '',
      entryValue: '',
      negCond: '',
      exitCond: '',
      visible: 'S',
      isolate: '',
      tipoRes: '',
    };

    // Establecer editingCondition y initialCondRule primero
    this.editingCondition = newCondition;
    this.initialCondRule = nextConditionCode; // Establecer directamente la propiedad

    // Forzar detección de cambios para asegurar que editingCondition y initialCondRule se propaguen
    this._cdr.detectChanges();

    // Usar setTimeout con un delay más largo para asegurar que el binding se actualice completamente
    setTimeout(() => {
      this.conditionDrawerVisible = true;
      // Forzar detección de cambios después de abrir el drawer
      this._cdr.detectChanges();
    }, 100);
  }

  async onNewAction(): Promise<void> {
    // Cerrar todos los drawers antes de abrir este
    this.closeAllDrawers();
    this.isEditModeAction = false;
    this.editingActionIndex = -1;
    this.editingAction = null;

    // Si no hay actionHeader, crear uno vacío para permitir editarlo (nueva regla o primera acción)
    if (!this.rule?.actionHeader && this.rule) {
      const effectiveLab = this.resolveEffectiveLabNr();
      if (effectiveLab == null) {
        this._wxrlService.handleError(new Error('WXRL.ERRORS.LAB_NR_REQUIRED'));
        return;
      }

      // Obtener el ruleNr desde el formulario o desde la regla
      const ruleNr = this.ruleForm?.value?.ruleNr || this.rule?.ruleNr || null;

      // Verificar si el ruleNr está en uso por otras acciones
      let proposedRuleActionNr = ruleNr;
      if (ruleNr) {
        // Cargar opciones de acciones si no están cargadas
        if (!this.ruleActionOptions || this.ruleActionOptions.length === 0) {
          await this.loadRuleActionOptions();
        }

        // Verificar si el ruleNr está en uso
        const isInUse = this.ruleActionOptions.some(opt => opt.value === ruleNr);

        // Si está en uso, dejar null para que el usuario lo edite manualmente
        if (isInUse) {
          proposedRuleActionNr = null;
        }
      }

      this.rule.actionHeader = {
        ruleActionNr: proposedRuleActionNr, // Asignar el ID de la regla como Nº Acción propuesto (o null si está en uso)
        ruleActionDesc: '',
        labNr: effectiveLab,
        info: '',
        author: null,
        createdWhen: null,
      };
    }

    this.actionDrawerVisible = true;
  }

  onNewTrigger(): void {
    // Cerrar todos los drawers antes de abrir este
    this.closeAllDrawers();
    this.isEditModeTrigger = false;
    this.editingTriggerIndex = -1;
    this.editingTrigger = null;

    this.triggerDrawerVisible = true;
  }

  onMoveUp(rowData: any): void {
    if (rowData.type === 'condition') {
      this.onMoveUpCondition(rowData);
    } else if (rowData.type === 'action') {
      this.onMoveUpAction(rowData);
    }
  }

  onMoveDown(rowData: any): void {
    if (rowData.type === 'condition') {
      this.onMoveDownCondition(rowData);
    } else if (rowData.type === 'action') {
      this.onMoveDownAction(rowData);
    }
  }

  isFirstItem(rowData: any): boolean {
    if (rowData.type === 'condition') {
      if (!this.conditionsFormArray || !rowData.condition) return true;
      const index = this.conditionsFormArray.controls.findIndex(
        (control) =>
          control.value.posC === rowData.condition.posC &&
          control.value.condRule === rowData.condition.condRule &&
          control.value.methName === rowData.condition.methName
      );
      return index <= 0;
    } else if (rowData.type === 'action') {
      if (!this.actionsFormArray || !rowData.action) return true;
      const index = this.actionsFormArray.controls.findIndex(
        (control) =>
          control.value.posA === rowData.action.posA &&
          control.value.ruleActionDesc === rowData.action.ruleActionDesc &&
          control.value.actionKrz === rowData.action.actionKrz
      );
      return index <= 0;
    } else if (rowData.type === 'trigger') {
      if (!this.triggersFormArray || !rowData.trigger) return true;
      const index = this.triggersFormArray.controls.findIndex(
        (control) =>
          control.value.posR === rowData.trigger.posR &&
          control.value.subjectID === rowData.trigger.subjectID &&
          control.value.subjectType === rowData.trigger.subjectType
      );
      return index <= 0;
    }
    return true;
  }

  isLastItem(rowData: any): boolean {
    if (rowData.type === 'condition') {
      if (!this.conditionsFormArray || !rowData.condition) return true;
      const index = this.conditionsFormArray.controls.findIndex(
        (control) =>
          control.value.posC === rowData.condition.posC &&
          control.value.condRule === rowData.condition.condRule &&
          control.value.methName === rowData.condition.methName
      );
      return index >= this.conditionsFormArray.length - 1;
    } else if (rowData.type === 'action') {
      if (!this.actionsFormArray || !rowData.action) return true;
      const index = this.actionsFormArray.controls.findIndex(
        (control) =>
          control.value.posA === rowData.action.posA &&
          control.value.ruleActionDesc === rowData.action.ruleActionDesc &&
          control.value.actionKrz === rowData.action.actionKrz
      );
      return index >= this.actionsFormArray.length - 1;
    } else if (rowData.type === 'trigger') {
      if (!this.triggersFormArray || !rowData.trigger) return true;
      const index = this.triggersFormArray.controls.findIndex(
        (control) =>
          control.value.posR === rowData.trigger.posR &&
          control.value.subjectID === rowData.trigger.subjectID &&
          control.value.subjectType === rowData.trigger.subjectType
      );
      return index >= this.triggersFormArray.length - 1;
    }
    return true;
  }

  onMoveUpCondition(rowData: any): void {
    if (!this.conditionsFormArray || !rowData.condition) return;

    const index = this.conditionsFormArray.controls.findIndex(
      (control) =>
        control.value.posC === rowData.condition.posC &&
        control.value.condRule === rowData.condition.condRule &&
        control.value.methName === rowData.condition.methName
    );

    if (index > 0) {
      const current = this.conditionsFormArray.at(index);
      const previous = this.conditionsFormArray.at(index - 1);

      this.conditionsFormArray.setControl(index, previous);
      this.conditionsFormArray.setControl(index - 1, current);

      // Reordenar posiciones
      this.conditionsFormArray.controls.forEach((control, idx) => {
        control.patchValue({ posC: idx });
      });

      // Sincronizar y reconstruir
      this.syncRuleFromFormArrays();
      setTimeout(() => {
        this.buildTree();
        this._cdr.markForCheck();
      }, 0);
    }
  }

  onMoveDownCondition(rowData: any): void {
    if (!this.conditionsFormArray || !rowData.condition) return;

    const index = this.conditionsFormArray.controls.findIndex(
      (control) =>
        control.value.posC === rowData.condition.posC &&
        control.value.condRule === rowData.condition.condRule &&
        control.value.methName === rowData.condition.methName
    );

    if (index < this.conditionsFormArray.length - 1) {
      const current = this.conditionsFormArray.at(index);
      const next = this.conditionsFormArray.at(index + 1);

      this.conditionsFormArray.setControl(index, next);
      this.conditionsFormArray.setControl(index + 1, current);

      // Reordenar posiciones
      this.conditionsFormArray.controls.forEach((control, idx) => {
        control.patchValue({ posC: idx });
      });

      // Sincronizar y reconstruir
      this.syncRuleFromFormArrays();
      setTimeout(() => {
        this.buildTree();
        this._cdr.markForCheck();
      }, 0);
    }
  }

  onMoveUpAction(rowData: any): void {
    if (!this.actionsFormArray || !rowData.action) return;

    const index = this.actionsFormArray.controls.findIndex(
      (control) =>
        control.value.posA === rowData.action.posA &&
        control.value.ruleActionDesc === rowData.action.ruleActionDesc &&
        control.value.actionKrz === rowData.action.actionKrz
    );

    if (index > 0) {
      const current = this.actionsFormArray.at(index);
      const previous = this.actionsFormArray.at(index - 1);

      this.actionsFormArray.setControl(index, previous);
      this.actionsFormArray.setControl(index - 1, current);

      // Reordenar posiciones
      this.actionsFormArray.controls.forEach((control, idx) => {
        control.patchValue({ posA: idx });
      });

      // Sincronizar y reconstruir
      this.syncRuleFromFormArrays();
      setTimeout(() => {
        this.buildTree();
        this._cdr.markForCheck();
      }, 0);
    }
  }

  onMoveDownAction(rowData: any): void {
    if (!this.actionsFormArray || !rowData.action) return;

    const index = this.actionsFormArray.controls.findIndex(
      (control) =>
        control.value.posA === rowData.action.posA &&
        control.value.ruleActionDesc === rowData.action.ruleActionDesc &&
        control.value.actionKrz === rowData.action.actionKrz
    );

    if (index < this.actionsFormArray.length - 1) {
      const current = this.actionsFormArray.at(index);
      const next = this.actionsFormArray.at(index + 1);

      this.actionsFormArray.setControl(index, next);
      this.actionsFormArray.setControl(index + 1, current);

      // Reordenar posiciones
      this.actionsFormArray.controls.forEach((control, idx) => {
        control.patchValue({ posA: idx });
      });

      // Sincronizar y reconstruir
      this.syncRuleFromFormArrays();
      setTimeout(() => {
        this.buildTree();
        this._cdr.markForCheck();
      }, 0);
    }
  }

  onMoveUpTrigger(_rowData: any): void {
    // #538: no se permite reordenar disparadores.
    return;
  }

  onMoveDownTrigger(_rowData: any): void {
    // #538: no se permite reordenar disparadores.
    return;
  }

  private syncRuleFromFormArrays(): void {
    // Sincronizar condiciones
    if (this.conditionsFormArray) {
      this.rule.conditions = this.conditionsFormArray.controls.map(
        (control) => control.value
      );
    }
    // Sincronizar acciones
    if (this.actionsFormArray) {
      this.rule.actions = this.actionsFormArray.controls.map(
        (control) => (control as FormGroup).getRawValue ? (control as FormGroup).getRawValue() : control.value
      );
    }
    // Sincronizar disparadores
    if (this.triggersFormArray) {
      this.rule.triggers = this.triggersFormArray.controls.map(
        (control) => control.value
      );
    }
    // Sincronizar actionHeader
    if (this.ruleForm) {
      const actionHeaderControl = this.ruleForm.get('actionHeader');
      if (actionHeaderControl) {
        const actionHeaderValue = actionHeaderControl.value;
        if (actionHeaderValue && (actionHeaderValue.ruleActionNr || actionHeaderValue.ruleActionDesc)) {
          this.rule.actionHeader = actionHeaderValue;
        }
      }
    }
  }

  // Funciones de guardado desde los drawers
  onSaveConditionFromDrawer(conditionData: any): void {
    if (!this.conditionsFormArray) return;

    const conditionFormGroup = this._fb.group(conditionData);

    if (this.isEditModeCondition && this.editingConditionIndex >= 0) {
      this.conditionsFormArray.setControl(
        this.editingConditionIndex,
        conditionFormGroup
      );
    } else {
      // Modo creación: agregar nueva condición
      this.conditionsFormArray.push(conditionFormGroup);
      // Reordenar posiciones
      this.conditionsFormArray.controls.forEach((control, idx) => {
        control.patchValue({ posC: idx });
      });
    }

    this.syncRuleFromFormArrays();
    if (this.ruleForm) {
      this.ruleForm.markAsDirty();
    }
    setTimeout(() => {
      this.buildTree();
      this._cdr.markForCheck();
    }, 0);
    this.closeConditionDrawer();
  }

  // Funciones de guardado (mantenidas para compatibilidad, pero ahora se usan desde los drawers)
  onSaveCondition(): void {
    // Este método ya no se usa directamente, se llama desde el drawer
  }

  onSaveActionFromDrawer(data: any): void {
    if (!this.actionsFormArray) return;

    // #554: guardar solo cabecera sin forzar alta/edición de paso.
    if (data?.actionHeader && !data?.action) {
      const actionHeader = data.actionHeader;
      if (this.ruleForm) {
        const actionHeaderControl = this.ruleForm.get('actionHeader');
        if (actionHeaderControl) {
          actionHeaderControl.patchValue(actionHeader);
        }
        if (
          actionHeader.ruleActionNr !== undefined &&
          actionHeader.ruleActionNr !== null
        ) {
          this.ruleForm.patchValue({ ruleActionNr: actionHeader.ruleActionNr });
          if (this.rule) {
            this.rule.ruleActionNr = actionHeader.ruleActionNr;
          }
        }
      }
      this.syncRuleFromFormArrays();
      if (this.ruleForm) {
        this.ruleForm.markAsDirty();
      }
      setTimeout(() => {
        this.buildTree();
        this._cdr.markForCheck();
      }, 0);
      this.closeActionDrawer();
      return;
    }

    // El drawer ahora puede enviar { action, actionHeader } o solo actionData (compatibilidad)
    const incoming = data.action || data;
    const actionHeader = data.actionHeader;

    // Edición: fusionar con el paso ya en el FormArray. El drawer no incluye todos los campos
    // del modelo por paso (p. ej. ruleActionDesc); si solo usamos lo emitido, _fb.group pierde
    // claves y el array queda inconsistente / el guardado falla en silencio.
    let merged: Record<string, unknown> = { ...incoming };
    if (this.isEditModeAction && this.editingActionIndex >= 0) {
      const ctrl = this.actionsFormArray.at(this.editingActionIndex);
      if (ctrl) {
        const fg = ctrl as FormGroup;
        const existing = fg.getRawValue ? fg.getRawValue() : { ...fg.value };
        merged = { ...existing, ...incoming };
        // #557: posA del paso no debe cambiar al editar (campo disabled en drawer).
        if (existing?.posA !== undefined && existing?.posA !== null) {
          merged['posA'] = existing.posA;
        }
      }
    }

    if (
      !this.isEditModeAction &&
      (merged['posA'] === undefined || merged['posA'] === null)
    ) {
      merged['posA'] = this.nextActionPosA;
    }

    const actionFormGroup = this._fb.group(merged as any);

    if (this.isEditModeAction && this.editingActionIndex >= 0) {
      // Modo edición: actualizar acción existente
      this.actionsFormArray.setControl(
        this.editingActionIndex,
        actionFormGroup
      );
    } else {
      // Modo creación: agregar nueva acción
      this.actionsFormArray.push(actionFormGroup);
      // Solo reindexar 0..n si la regla ya usaba posA 0-based; API Siemens usa 1-based.
      if (!this.actionsPosAIsOneBased()) {
        this.actionsFormArray.controls.forEach((control, idx) => {
          control.patchValue({ posA: idx });
        });
      }
    }

    // Actualizar actionHeader en el ruleForm si existe
    if (actionHeader && this.ruleForm) {
      const actionHeaderControl = this.ruleForm.get('actionHeader');
      if (actionHeaderControl) {
        actionHeaderControl.patchValue(actionHeader);
      }

      // Mantener ruleActionNr sincronizado con la cabecera de acción seleccionada
      if (actionHeader.ruleActionNr !== undefined && actionHeader.ruleActionNr !== null) {
        this.ruleForm.patchValue({ ruleActionNr: actionHeader.ruleActionNr });
        if (this.rule) {
          this.rule.ruleActionNr = actionHeader.ruleActionNr;
        }
      }
    }

    this.syncRuleFromFormArrays();
    if (this.ruleForm) {
      this.ruleForm.markAsDirty();
    }
    setTimeout(() => {
      this.buildTree();
      this._cdr.markForCheck();
    }, 0);
    this.closeActionDrawer();
  }

  async onRequestLoadExistingAction(ruleActionNr: number): Promise<void> {
    if (!this.ruleForm) return;
    if (!ruleActionNr) return;

    const doLoad = async () => {
      const detail = await this._wxrlService.getRuleActionDetail(ruleActionNr);
      const actionHeader = detail?.actionHeader;
      const actions = detail?.actions || [];
      if (!actionHeader || !actionHeader.ruleActionNr) {
        throw new Error('Action header not found for selected RuleActionNr');
      }

      // Sustituir cabecera y pasos
      if (this.rule) {
        this.rule.actionHeader = actionHeader;
        this.rule.ruleActionNr = actionHeader.ruleActionNr;
        this.rule.actions = actions as any;
      }

      // Patch ruleForm
      this.ruleForm.patchValue({ ruleActionNr: actionHeader.ruleActionNr });
      const actionHeaderControl = this.ruleForm.get('actionHeader');
      if (actionHeaderControl) {
        actionHeaderControl.patchValue({
          ruleActionNr: actionHeader.ruleActionNr || null,
          ruleActionDesc: actionHeader.ruleActionDesc || '',
          labNr: actionHeader.labNr || null,
          createdWhen: actionHeader.createdWhen || null,
          info: actionHeader.info || '',
          author: actionHeader.author || null,
        });
      }

      // Reemplazar pasos
      this.actionsFormArray.clear();
      actions.forEach((a: any) => {
        this.actionsFormArray.push(
          this._fb.group({
            ruleState: [this.resolveActionStateToCode(a.ruleState)],
            posA: [a.posA],
            ruleActionDesc: [a.ruleActionDesc],
            actionKrz: [a.actionKrz],
            labelAction: [a.labelAction ?? ''],
            methNr: [a.methNr],
            grupNr: [a.grupNr],
            textNr: [a.textNr],
            actionValue: [a.actionValue],
            additional1: [a.additional1],
            additional2: [a.additional2],
            persNr: [a.persNr],
            kommentar: [a.kommentar],
          })
        );
      });

      this.ruleForm.markAsDirty();
      this.syncRuleFromFormArrays();
      setTimeout(() => {
        this.buildTree();
        this._cdr.markForCheck();
      }, 0);

      // Cerrar el drawer para evitar inconsistencias con el paso en edición
      this.closeActionDrawer();
    };

    const hasExisting =
      (this.actionsFormArray && this.actionsFormArray.length > 0) ||
      !!this.rule?.actionHeader?.ruleActionNr;

    if (hasExisting) {
      this._confirmationService.confirm({
        message: this._translate.instant('WXRL.ACTION.CONFIRM_REPLACE_ACTION'),
        header: this._translate.instant('WXRL.EDIT_MODE.CONFIRM_HEADER'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this._translate.instant('OK'),
        rejectLabel: this._translate.instant('CANCEL'),
        accept: () => {
          doLoad().catch((error) => {
            console.error('Error loading existing action:', error);
            this._wxrlService.handleError(error);
          });
        },
      });
    } else {
      try {
        await doLoad();
      } catch (error) {
        console.error('Error loading existing action:', error);
        this._wxrlService.handleError(error);
      }
    }
  }

  onSaveAction(): void {
    // Este método ya no se usa directamente, se llama desde el drawer
  }

  onSaveTriggerFromDrawer(triggerData: any): void {
    if (!this.triggersFormArray) return;

    const triggerFormGroup = this._fb.group(triggerData);

    if (this.isEditModeTrigger && this.editingTriggerIndex >= 0) {
      // Modo edición: actualizar disparador existente
      this.triggersFormArray.setControl(
        this.editingTriggerIndex,
        triggerFormGroup
      );
    } else {
      // Modo creación: agregar nuevo disparador
      const nextPosR = this.triggersFormArray.controls.reduce((maxPosR, control) => {
        const currentPosR = Number(control.value?.posR);
        if (!Number.isFinite(currentPosR)) {
          return maxPosR;
        }
        return Math.max(maxPosR, currentPosR);
      }, 0) + 1;
      triggerFormGroup.patchValue({ posR: nextPosR });
      this.triggersFormArray.push(triggerFormGroup);
    }

    this.syncRuleFromFormArrays();
    if (this.ruleForm) {
      this.ruleForm.markAsDirty();
    }
    setTimeout(() => {
      this.buildTree();
      this._cdr.markForCheck();
    }, 0);
    this.closeTriggerDrawer();
  }

  onSaveTrigger(): void {
    // Este método ya no se usa directamente, se llama desde el drawer
  }

  // Cerrar todos los drawers
  private closeAllDrawers(): void {
    this.conditionDrawerVisible = false;
    this.actionDrawerVisible = false;
    this.triggerDrawerVisible = false;
    this.headerDrawerVisible = false;
    this.isEditModeCondition = false;
    this.isEditModeAction = false;
    this.isEditModeTrigger = false;
    this.editingConditionIndex = -1;
    this.editingActionIndex = -1;
    this.editingTriggerIndex = -1;
    this.editingCondition = null;
    this.editingAction = null;
    this.editingTrigger = null;
    this.editingHeaderData = null;
  }

  // Funciones de cierre de drawers
  closeConditionDrawer(): void {
    this.conditionDrawerVisible = false;
    this.isEditModeCondition = false;
    this.editingConditionIndex = -1;
    this.editingCondition = null;
    this.initialCondRule = null; // Resetear initialCondRule cuando se cierra el drawer
  }

  closeActionDrawer(): void {
    this.actionDrawerVisible = false;
    this.isEditModeAction = false;
    this.editingActionIndex = -1;
    this.editingAction = null;
  }

  closeTriggerDrawer(): void {
    this.triggerDrawerVisible = false;
    this.isEditModeTrigger = false;
    this.editingTriggerIndex = -1;
    this.editingTrigger = null;
  }

  // Getters para títulos de drawers
  get conditionDrawerTitle(): string {
    return this.isEditModeCondition
      ? this._translate.instant('WXRL.SCHEMA.EDIT_CONDITION')
      : this._translate.instant('WXRL.SCHEMA.NEW_CONDITION');
  }

  get actionDrawerTitle(): string {
    return this.isEditModeAction
      ? this._translate.instant('WXRL.SCHEMA.EDIT_ACTION')
      : this._translate.instant('WXRL.SCHEMA.NEW_ACTION');
  }

  get triggerDrawerTitle(): string {
    return this.isEditModeTrigger
      ? this._translate.instant('WXRL.SCHEMA.EDIT_TRIGGER')
      : this._translate.instant('WXRL.SCHEMA.NEW_TRIGGER');
  }

  // Métodos para editar cabecera
  async onEditHeader(): Promise<void> {
    if (!this.ruleForm) return;

    // Cargar valores actuales del formulario principal
    const currentValues = this.ruleForm.value;

    // Cerrar todos los drawers antes de abrir este
    this.closeAllDrawers();

    // Asignar editingHeaderData ANTES de cambiar headerDrawerVisible a true
    this.editingHeaderData = {
      ruleNr: currentValues.ruleNr || 0,
      ruleName: currentValues.ruleName || '',
      ruleKrz: currentValues.ruleKrz || '',
      kommentar: currentValues.kommentar || '',
      condition: currentValues.condition || '',
      negRule: currentValues.negRule || false,
      directExecution: !!currentValues.directExecution,
    };

    // Usar setTimeout para asegurar que editingHeaderData se propague antes de abrir el drawer
    setTimeout(() => {
      this.headerDrawerVisible = true;
    }, 0);
  }

  onSaveHeaderFromDrawer(headerData: any): void {
    if (!this.ruleForm) return;

    const patchData: any = {
      ruleName: headerData.ruleName,
      ruleKrz: headerData.ruleKrz,
      kommentar: headerData.kommentar,
      condition: headerData.directExecution
        ? ''
        : headerData.condition || '',
      negRule: headerData.negRule,
      directExecution: !!headerData.directExecution,
    };

    if (this.isNewRule && headerData.ruleNr != null && headerData.ruleNr !== '') {
      const prevRuleNr = this.ruleForm.get('ruleNr')?.value;
      const nextNr = Number(headerData.ruleNr);
      if (
        prevRuleNr != null &&
        Number(prevRuleNr) !== nextNr &&
        this.labNr != null
      ) {
        try {
          localStorage.removeItem(`wxrl-rule-new-${this.labNr}-${prevRuleNr}`);
        } catch {
          /* ignore */
        }
      }
      patchData.ruleNr = nextNr;
    }

    this.ruleForm.patchValue(patchData);
    this.ruleForm.markAsDirty();

    if (this.rule) {
      this.rule.ruleName = headerData.ruleName;
      this.rule.ruleKrz = headerData.ruleKrz;
      this.rule.kommentar = headerData.kommentar;
      this.rule.condition = headerData.directExecution
        ? WXRL_DIRECT_CONDITION
        : headerData.condition;
      this.rule.negRule = headerData.negRule;
      if (this.isNewRule && patchData.ruleNr != null) {
        this.rule.ruleNr = patchData.ruleNr;
      }
    }

    this.closeHeaderDrawer();
    if (this.isNewRule) {
      queueMicrotask(() => {
        this.headerDrawerFirstAccessUx = false;
        this._cdr.markForCheck();
      });
    }
    setTimeout(() => {
      this.buildTree();
      this._cdr.markForCheck();
    }, 0);
  }

  onClearConditionsFromHeaderDrawer(): void {
    if (!this.conditionsFormArray) {
      return;
    }
    this.conditionsFormArray.clear();
    if (this.rule) {
      this.rule.conditions = [];
    }
    if (this.ruleForm) {
      this.ruleForm.markAsDirty();
      queueMicrotask(() => {
        this.ruleForm.updateValueAndValidity({ emitEvent: false });
        this.scheduleBuildTree();
      });
    } else {
      this.scheduleBuildTree();
    }
    this._cdr.markForCheck();
  }

  closeFormulaDrawer(): void {
    this.formulaDrawerVisible = false;
  }

  /**
   * Obtiene las condiciones disponibles para el editor de fórmulas.
   *
   * @returns {Array<{condRule: string, kommentar: string}>} Array de condiciones con ID y descripción
   */
  getConditionsForFormulaEditor(): Array<{ condRule: string; kommentar: string }> {
    if (!this.conditionsFormArray) {
      return [];
    }
    return this.conditionsFormArray.controls.map((control) => ({
      condRule: control.value.condRule || '',
      kommentar: control.value.kommentar || control.value.methName || '',
    }));
  }

  onSaveHeader(): void {
    // Este método ya no se usa directamente, se llama desde el drawer
  }

  closeHeaderDrawer(): void {
    this.headerDrawerVisible = false;
    this.editingHeaderData = null;
  }

  onSaveFormulaAndFinish(formulaData: { condition: string; negRule: boolean }): void {
    if (!this.ruleForm) return;

    // Actualizar el formulario principal con la fórmula
    this.ruleForm.patchValue({
      condition: formulaData.condition || '',
      negRule: formulaData.negRule || false,
      directExecution: false,
    });
    this.ruleForm.markAsDirty();

    // Actualizar también el objeto rule
    if (this.rule) {
      this.rule.condition = formulaData.condition || '';
      this.rule.negRule = formulaData.negRule || false;
    }

    // Cerrar el drawer de fórmula
    this.formulaDrawerVisible = false;

    // El guardado final se hace desde el componente padre (wxrl-rule-detail)
    // cuando el usuario hace clic en "Guardar" en la página
  }

}
